import React from "react";
import { Alert } from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import SnoozeIcon from "@mui/icons-material/Snooze";
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LoopIcon from '@mui/icons-material/Loop';

interface DataStatusAlertProps {
  status: string;
}

const DataStatusAlert: React.FC<DataStatusAlertProps> = ({ status }) => {
  // Adjusting the logic for icons, mapping status to appropriate icons.
  const iconMapping: Record<string, React.ReactNode> = {
    Done: (
      <CheckCircleOutlineOutlinedIcon
        style={{ color: "#1E4620", fontSize: "17" }}
      />
    ),
    "In Progress": <CachedIcon style={{ color: "#7B1FA2", fontSize: "17" }} />,
    Snoozed: <SnoozeIcon style={{ color: "#5a5a5a", fontSize: "17" }} />,
    "To Do": <ErrorOutlineIcon style={{ color: "#5F2120", fontSize: "17" }} />,
    Freeze: <AcUnitIcon style={{ color: "#014361", fontSize: "17" }} />,
    default: <SnoozeIcon style={{ color: "#5a5a5a", fontSize: "17" }} />,
    publish: (
      <CheckCircleOutlineOutlinedIcon
        style={{ color: "#1E4620", fontSize: "17" }}
      />
    ),
    draft: (
      <PendingOutlinedIcon
        style={{ color: "#014361", fontSize: "17" }}
      />
    ),
    Active: (
      <CheckCircleOutlinedIcon
        style={{ color: "#1E4620", fontSize: "17" }}
      />
    ),
    Inactive: (
      <CancelOutlinedIcon
        style={{ color: "#5F2120", fontSize: "17" }}
      />
    ),
    Running: (
      <LoopIcon
        style={{ color: "#7B1FA2", fontSize: "17" }}
      />
    ),
    Failed: (
      <CancelOutlinedIcon
        style={{ color: "#5F2120", fontSize: "17" }}
      />
    ),
    Failure: (
      <CancelOutlinedIcon
        style={{ color: "#5F2120", fontSize: "17" }}
      />
    ),
    Cancelled: (
      <CancelOutlinedIcon
        style={{ color: "#5F2120", fontSize: "17" }}
      />
    ),
    Success: (
      <CheckCircleOutlineOutlinedIcon
        style={{ color: "#1E4620", fontSize: "17" }}
      />
    ),
  };

  const statusColors: Record<string, string> = {
    Done: "#1E4620", // Dark Green
    Active: "#1E4620", // Dark Green
    Inactive: "#5F2120", // Dark Green
    "In Progress": "#6A1B9A", // Dark Purple
    Snoozed: "#5a5a5a", // Dark Gray
    "To Do": "#5F2120", // Dark Red
    Freeze: "#014361", // Dark Blue
    publish: "#1E4620", // Dark Green
    draft: "#014361", // Dark Blue
    default: "#000000", // Default Black
    Success: "#1E4620", // Dark Green
    Running: "#7B1FA2", // Dark Purple
    Failed: "#5F2120", // Dark Red
    Failure: "#5F2120", // Dark Red
    Cancelled: "#5F2120", // Dark Gray

  };

  const borderColor: Record<string, string> = {
    Done: "#8dba8f", // Dark Green Border
    Active: "#2E7D3280", // Dark Green
    Inactive: "#D32F2F80", // Dark Green
    "In Progress": "#c786d2", // Dark Purple Border
    Snoozed: "#9e9e9e", // Dark Gray Border
    "To Do": "#e88e8e", // Dark Red Border
    Freeze: "#83c6f8", // Dark Blue Border
    publish: "#2E7D3280", // Dark Green
    draft: "#2196F380", // Dark Blue
    default: "#000000", // Default Black Border
    Success: "#1E4620", // Dark Green
    Running: "#7B1FA2", // Dark Purple
    Failed: "#5F2120", // Dark Red
    Failure: "#5F2120", // Dark Red
    Cancelled: "#e88e8e", // Dark Gray
  };

  const backgroundColor: Record<string, string> = {
    Done: "#EDF7ED", // Light Green
    Active: "#EDF7ED", // Dark Green
    Inactive: "#FDEDED", // Dark Green
    "In Progress": "#f3e5f5", // Light Orange
    Snoozed: "#e0e0e0", // Light Blue
    "To Do": "#FDEDED", // Light Red
    Freeze: "#e5f6fd", // Light Purple
    publish: "#EDF7ED", // Light Green
    draft: "#E5F6FD", // Light Blue
    default: "#E0E0E0", // Default Gray
    Success: "#EDF7ED", // Light Green
    Running: "#9C27B01F", // Light Purple
    Failed: "#FDEDED", // Light Red
    Failure: "#FDEDED", // Light Red
    Cancelled: "#FDEDED", // Dark Gray
  };

  return (
    <Alert
      icon={iconMapping[status] || iconMapping.default}
      sx={{
        height: "25px",
        display: "flex",
        alignItems: "center",
        width: "100%",
        paddingTop: "2px",
        paddingBottom: "2px",
        paddingLeft: "4px",
        paddingRight: "4px",
        fontSize: "13px",
        lineHeight: "1.2",
        whiteSpace: "nowrap",
        color: statusColors[status] || statusColors.default,
        border: `1px solid ${borderColor[status] || borderColor.default}`,
        backgroundColor: backgroundColor[status] || backgroundColor.default,
        ".MuiAlert-icon": { marginRight: "5px", alignSelf: "center" },
        ".MuiAlert-message": { 
          display: "flex", 
          alignItems: "center", 
          padding: 0,
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
      }}
      variant="outlined"
      severity={
        status === "Done"
          ? "success"
          : status === "In Progress"
          ? "warning"
          : status === "Snoozed"
          ? "info"
          : status === "To Do"
          ? "error"
          : status === "Freeze"
          ? "info"
          : status === "publish"
          ? "success"
          : status === "draft"
          ? "success"
          : "info" // Default severity
      }
    >
      {status === "publish" ? "Published" : status.charAt(0).toUpperCase() + status.slice(1)}
    </Alert>
  );
};

export default DataStatusAlert;
